import { useEffect, useRef, useState } from 'react';

/**
 * Scroll-linked object-position for SEO discovery hero images (venue / city / performer).
 * As the hero moves through the viewport, more of the image is revealed via object-position.
 */
const useHeroImagePosition = () => {
  const ref = useRef(null);
  const [position, setPosition] = useState(50);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    if (prefersReducedMotion) {
      return undefined;
    }

    let frame = null;
    const update = () => {
      frame = null;
      const el = ref.current;
      if (!el) {
        return;
      }

      const rect = el.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 1;
      const progress = Math.min(
        1,
        Math.max(0, (viewportHeight - rect.top) / (viewportHeight + rect.height))
      );
      setPosition(Math.round(28 + progress * 44));
    };

    const requestUpdate = () => {
      if (!frame) {
        frame = window.requestAnimationFrame(update);
      }
    };

    update();
    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);

    return () => {
      if (frame) {
        window.cancelAnimationFrame(frame);
      }
      window.removeEventListener('scroll', requestUpdate);
      window.removeEventListener('resize', requestUpdate);
    };
  }, []);

  return { ref, position };
};

export default useHeroImagePosition;
